import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, ShoppingBag } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import CartDrawer from "@/components/site/CartDrawer";
import MerchDetailModal from "@/components/site/MerchDetailModal";
import MerchQuickCard from "@/components/site/MerchQuickCard";
import BundleDeals, { useBundles } from "@/components/site/BundleDeals";
import PromoStrip from "@/components/site/PromoStrip";
import useAutoRefreshList from "@/hooks/useAutoRefreshList";
import { Button } from "@/components/ui/button";

const TRENDING = "trending";
const ALL = "all";

function bannerStyle(b) {
  const c2 = b.bg_color_2 || b.bg_color;
  return { background: `linear-gradient(120deg, ${b.bg_color} 0%, ${c2} 100%)`, color: b.text_color };
}

function Banner({ banner, index, onPick, popping }) {
  const full = banner.size === "full";
  return (
    <motion.button
      type="button"
      data-testid={`merch-banner-${banner.id}`}
      layout
      initial={{ opacity: 0, y: 70, rotate: index % 2 ? 1.2 : -1.2, scale: 0.96 }}
      animate={popping ? { opacity: 0, scale: 1.12, y: -20, transition: { duration: 0.35, ease: "easeIn" } } : { opacity: 1, y: 0, rotate: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 16, delay: 0.12 + index * 0.13 }}
      whileHover={{ y: -6, scale: 1.01 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onPick(banner)}
      style={bannerStyle(banner)}
      className={`relative text-left overflow-hidden rounded-2xl shadow-[0_24px_60px_-30px_rgba(12,46,43,0.55)] group ${full ? "md:col-span-2 min-h-[260px] lg:min-h-[320px]" : "min-h-[200px] lg:min-h-[240px]"}`}
    >
      <div className="absolute inset-0 grain-bg opacity-15 pointer-events-none" />
      {banner.image_url && banner.image_style === "cover" && (
        <>
          <img src={banner.image_url} alt="" data-testid={`merch-banner-image-${banner.id}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(100deg, ${banner.bg_color} 0%, ${banner.bg_color}E6 35%, ${(banner.bg_color_2 || banner.bg_color)}40 100%)` }} />
        </>
      )}
      {banner.image_url && banner.image_style !== "cover" && (
        <img src={banner.image_url} alt="" data-testid={`merch-banner-image-${banner.id}`} className="absolute inset-y-0 right-0 w-[55%] h-full object-cover [mask-image:linear-gradient(to_right,transparent,black_40%)] transition-transform duration-700 group-hover:scale-105" />
      )}
      <div className="absolute -right-10 -bottom-16 w-56 h-56 rounded-full opacity-25 blur-2xl pointer-events-none transition-transform duration-700 group-hover:scale-125" style={{ background: banner.accent_color }} />
      <div className={`relative p-7 lg:p-10 flex flex-col justify-between h-full ${full ? "max-w-2xl" : "max-w-md"}`}>
        <div className="inline-flex items-center gap-2 font-tactical text-[11px] uppercase tracking-[0.3em]" style={{ color: banner.accent_color }}>
          <span className="w-6 h-[2px]" style={{ background: banner.accent_color }} />
          {banner.kind === TRENDING ? "Hot right now" : "Department"}
        </div>
        <div className="mt-6">
          <h2 className={`font-display font-black leading-[0.95] tracking-tight ${full ? "text-4xl sm:text-5xl lg:text-6xl" : "text-3xl sm:text-4xl"}`}>{banner.title}</h2>
          {banner.subtitle && <p className="mt-3 text-sm sm:text-base opacity-80 max-w-md">{banner.subtitle}</p>}
        </div>
        <div className="mt-6 inline-flex items-center gap-2 font-bold text-sm px-4 py-2 rounded-full w-fit transition-transform duration-300 group-hover:translate-x-1" style={{ background: banner.accent_color, color: banner.bg_color }}>
          {banner.kind === TRENDING ? <Sparkles className="w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />} Shop {banner.kind === TRENDING ? "the drop" : banner.title} <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </motion.button>
  );
}

export default function MerchPage() {
  const { items } = useAutoRefreshList("/merch");
  const [layout, setLayout] = useState({ departments: [], banners: [] });
  const [params, setParams] = useSearchParams();
  const dept = params.get("dept") || ALL;
  const [popping, setPopping] = useState(null);
  const [detail, setDetail] = useState(null);
  const bundles = useBundles();
  const bundleFor = (pid) => bundles.find((b) => b.product_ids.includes(pid));
  const gridRef = useRef(null);

  useEffect(() => {
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/merch/layout`).then((r) => r.json()).then(setLayout).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const deptName = useMemo(() => {
    if (dept === ALL) return "All merch";
    if (dept === TRENDING) return "Latest Trending";
    return layout.departments.find((d) => d.id === dept)?.name || "Department";
  }, [dept, layout.departments]);

  const visible = useMemo(() => {
    if (dept === ALL) return items;
    if (dept === TRENDING) {
      const flagged = items.filter((p) => p.trending);
      if (flagged.length) return flagged;
      return [...items].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 8);
    }
    return items.filter((p) => p.department_id === dept);
  }, [items, dept]);

  const select = (key) => {
    const next = new URLSearchParams(params);
    if (key === ALL) next.delete("dept"); else next.set("dept", key);
    setParams(next, { replace: true });
  };
  const goToBundle = (b) => {
    const wasAll = dept === ALL;
    if (!wasAll) select(ALL);
    setTimeout(() => {
      const el = document.getElementById(`bundle-${b.id}`);
      if (el) { window.history.replaceState(null, "", `#bundle-${b.id}`); el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-4", "ring-[#FFDF00]"); setTimeout(() => el.classList.remove("ring-4", "ring-[#FFDF00]"), 2200); }
    }, wasAll ? 50 : 500);
  };

  const pickBanner = (b) => {
    setPopping(b.id);
    setTimeout(() => {
      select(b.kind === TRENDING ? TRENDING : b.department_id || ALL);
      setPopping(null);
      setTimeout(() => gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
    }, 330);
  };

  const tabs = [{ key: ALL, label: "All" }, { key: TRENDING, label: "Latest Trending" }, ...layout.departments.map((d) => ({ key: d.id, label: d.name }))];

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="merch-page">
      <Header />
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Link to="/" data-testid="merch-back-home" className="inline-flex items-center gap-1 text-sm font-bold text-[#0C2E2B] hover:underline"><ArrowLeft className="w-4 h-4" /> Back home</Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-tactical text-[11px] uppercase tracking-[0.3em] text-[#0C2E2B]/60">Harvey's Place · Big &amp; Tall</div>
              <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#0C2E2B] tracking-tight leading-none mt-1">The Shop</h1>
            </div>
            <p className="text-sm text-zinc-500 max-w-sm">Built for people who move heavy things. Pick a department or jump on the latest drop.</p>
          </motion.div>
          <PromoStrip />
        </div>

        <div className="sticky top-16 z-30 mt-6 bg-[#FAF8F5]/85 backdrop-blur-md border-y border-zinc-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 overflow-x-auto py-3 no-scrollbar" data-testid="merch-dept-tabs" role="tablist">
            {tabs.map((t) => {
              const active = dept === t.key;
              return (
                <button key={t.key} role="tab" aria-selected={active} data-testid={`merch-dept-tab-${t.key}`} onClick={() => select(t.key)}
                  className={`relative whitespace-nowrap px-4 h-9 rounded-full text-xs font-black uppercase tracking-wider transition-colors ${active ? "text-[#0C2E2B]" : "text-zinc-500 hover:text-[#0C2E2B]"}`}>
                  {active && <motion.span layoutId="dept-pill" className="absolute inset-0 rounded-full bg-[#FFDF00] shadow-[0_8px_20px_-10px_rgba(12,46,43,0.5)]" transition={{ type: "spring", stiffness: 350, damping: 30 }} />}
                  <span className="relative">{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence initial={false}>
            {dept === ALL && layout.banners.length > 0 && (
              <motion.section key="banners" data-testid="merch-banners" exit={{ opacity: 0, height: 0, transition: { duration: 0.3 } }} className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-8 overflow-hidden">
                {layout.banners.map((b, i) => <Banner key={b.id} banner={b} index={i} onPick={pickBanner} popping={popping === b.id} />)}
              </motion.section>
            )}
          </AnimatePresence>

          {dept === "all" && <BundleDeals bundles={bundles} />}

          <section ref={gridRef} className="py-12 scroll-mt-32" data-testid="merch-grid-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="font-tactical text-[11px] uppercase tracking-[0.25em] text-[#0C2E2B]/60">{dept === ALL ? "Everything" : "Now browsing"}</div>
                <h2 className="font-display font-black text-2xl sm:text-3xl text-[#0C2E2B]" data-testid="merch-grid-title">{deptName} <span className="text-zinc-400 text-lg font-bold">· {visible.length}</span></h2>
              </div>
              {dept !== ALL && <Button variant="outline" data-testid="merch-back-to-all" onClick={() => select(ALL)} className="rounded-full h-9 text-xs font-bold border-[#0C2E2B]"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> All departments</Button>}
            </div>
            {visible.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="rounded-2xl border-2 border-dashed border-zinc-300 p-12 text-center text-zinc-500" data-testid="merch-empty">
                <ShoppingBag className="w-8 h-8 mx-auto mb-2 text-zinc-400" /> Nothing in {deptName} yet — check back soon.
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="merch-grid">
                <AnimatePresence mode="popLayout">
                  {visible.map((p, idx) => <MerchQuickCard key={p.id} p={p} idx={idx} onDetails={setDetail} bundle={bundleFor(p.id)} onBundle={goToBundle} />)}
                </AnimatePresence>
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
      <CartDrawer />
      <MerchDetailModal product={detail} open={!!detail} onClose={() => setDetail(null)} />
    </div>
  );
}
